"use client";

import { fixedTableTemplates } from "@/lib/server/constant";
import { TResume } from "@/lib/store/resume";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";

type Props = {
  mode?: "preview" | "builder" | "print";
  marginPx?: number;
  resume?: TResume;
  pageNumber: number;
  children: React.ReactNode;
  onPagesChange?: (pageCount: number) => void;
};

const pageSizeMap = {
  a4: {
    width: 210,
    height: 297,
  },
  letter: {
    width: 216,
    height: 279,
  },
};

export const MM_TO_PX = 3.78;

export const Page = ({
  marginPx = 0,
  mode = "preview",
  pageNumber,
  children,
  resume,
  onPagesChange,
}: Props) => {
  const fontFamily = "IBM Plex Serif";

  const magin = fixedTableTemplates.includes(
    resume?.data?.settings?.template || "",
  )
    ? 13
    : marginPx;

  const [pages, setPages] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const PAGE_HEIGHT = pageSizeMap.a4.height * MM_TO_PX;
  const height = containerRef.current?.offsetHeight;

  useEffect(() => {
    if (containerRef.current) {
      const pageNumber = Math.ceil(
        containerRef.current.offsetHeight / PAGE_HEIGHT,
      );

      setPages(Array.from({ length: pageNumber }, () => "1"));
      onPagesChange?.(pageNumber);
    }
  }, [containerRef.current, height, resume]);

  return (
    <div id="resume-main">
      {pages.map((res, i) => {
        const firstPageOrLastHeight = pageSizeMap.a4.height * MM_TO_PX - magin;

        const height = pageSizeMap.a4.height * MM_TO_PX - 2 * magin;

        const getTranslateYHeight = () => {
          let result = firstPageOrLastHeight;
          if (pages.length === 1) {
            result = 0;
          } else if (pages.length === 2) {
            result = i * firstPageOrLastHeight;
          } else if (pages.length > 2 && i !== 0 && i !== pages.length - 1) {
            result = (i - 1) * height + firstPageOrLastHeight;
            return result;
          } else if (pages.length - 1 === i) {
            return (pages.length - 2) * height + firstPageOrLastHeight;
          }
          return i * result;
        };
        return (
          <div key={i}>
            <div
              data-page={i + 1}
              className={classNames(
                "relative bg-white",
                i !== 0 && "pt-custom",
                i !== pages.length - 1 && "pb-custom",
                mode === "preview" && "z-[2]",
                mode !== "print" && "shadow-small",
              )}
            >
              <div
                key="i"
                id="content"
                style={{
                  fontFamily,
                  width: `${pageSizeMap.a4.width * MM_TO_PX}px`,
                  height: `${i !== 0 && i !== pages.length - 1 ? height : firstPageOrLastHeight}px`,
                  overflow: "hidden",
                  minHeight: pages.length === 1 ? `${PAGE_HEIGHT}px` : "auto",
                }}
              >
                <div
                  style={{
                    transform: `translateY(-${getTranslateYHeight()}px)`,
                  }}
                >
                  {children}
                </div>
              </div>
              {pages.length !== 1 &&
                resume?.data.settings?.page.options.pageNumbers && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 transform text-xs font-bold text-black">
                    {i + 1}
                  </div>
                )}
            </div>

            {mode !== "print" && <div className="h-[8px] w-full"></div>}
          </div>
        );
      })}

      <div
        className={classNames(
          "relative  bg-white ",
          mode !== "print" ? "h-0 overflow-hidden" : "",
        )}
        style={{
          fontFamily,
          width: `${pageSizeMap.a4.width * MM_TO_PX}px`,
        }}
        data-print="main"
      >
        <div ref={containerRef}>{children}</div>
        {/* {mode === "builder" && page.options.breakLine && (
          <div
            className="absolute inset-x-0 border-b border-dashed"
            style={{
              top: `${pageSizeMap.a4.height * MM_TO_PX}px`,
            }}
          />
        )} */}
      </div>
    </div>
  );
};
